import React, { useCallback, useEffect, useState } from 'react';

import { Box, useMediaQuery, useTheme } from '@mui/material';

import noop from 'lodash/noop';

import { useModelContext } from '../../context/modelContext.ts';
import { useAppDispatch } from '../../hooks';
import { toastReducer } from '../../store/reducers/toast.reducer';
import { LoadingSpinner } from '../loading/LoadingSpinner';
import { ModelLayer } from '../model-context/ModelContext.ts';
import { ModelPreviewScene } from '../model-preview/ModelPreviewScene.tsx';
import { SimpleScene } from '../model-preview/SimpleScene.tsx';
import { ModelScaleBottomBar } from './ModelScaleBottomBar';
import ProjectSettings from './ProjectSettings';
import SetupProject from './SetupProject';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  if (value !== index) return null;

  return (
    <div
      role="tabpanel"
      id={`wizard-tabpanel-${index}`}
      aria-labelledby={`wizard-tab-${index}`}
      {...other}
      style={{ width: '100%', height: '100%' }}
    >
      <Box sx={{ width: '100%', height: '100%' }}>{children}</Box>
    </div>
  );
}

export default function ProjectWizard() {
  const [activeStep, setActiveStep] = useState(0);
  const dispatch = useAppDispatch();
  const {
    uploadedModels,
    modelLoadingError,
    setModelLoadingError,
    addModelLayers,
    setMetersPerUnit,
    setScaleCalibrated,
  } = useModelContext();
  const [step1LoadedModels, setStep1LoadedModels] = useState<Record<string, boolean>>({});
  const [scalePickActive, setScalePickActive] = useState(false);
  const [pickedUnitDistance, setPickedUnitDistance] = useState<number | null>(null);
  const [scaleBarOpen, setScaleBarOpen] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const hasUploadedModels = (uploadedModels?.length ?? 0) > 0;
  const areModelsLoaded = hasUploadedModels && (uploadedModels ?? []).every((m) => step1LoadedModels[m.name]);

  // Fingerprint the uploaded-models list so we auto-open the scale bar whenever
  // the list actually changes (new file, replacement, example selected).
  const modelsFingerprint = uploadedModels?.map((m) => `${m.name}:${m.lastModified ?? ''}:${m.size ?? ''}`).join('|');

  // Reset loaded state whenever the model list changes
  useEffect(() => {
    setStep1LoadedModels({});
  }, [modelsFingerprint]);

  // Reset transient bar state when the user clears all uploaded models.
  // The bar is never auto-opened — the default 1 unit = 1 m is a valid scale,
  // and the sidebar summary row provides the entry point via its edit button.
  useEffect(() => {
    if (!hasUploadedModels) {
      setScaleBarOpen(false);
      setScalePickActive(false);
      setPickedUnitDistance(null);
    }
  }, [hasUploadedModels]);

  const handleBack = useCallback(() => {
    setStep1LoadedModels({});
    setActiveStep((prev) => Math.max(prev - 1, 0));
  }, []);

  const handleStep1LayersFound = useCallback(
    (modelName: string, layers: ModelLayer[]) => {
      const originalNames = layers.map((l) => l.originalName);
      const uniqueNames = new Set(originalNames);

      if (uniqueNames.size !== originalNames.length) {
        dispatch(
          toastReducer.actions.showToast({
            message: 'projects.cadProject.duplicateLayerNames',
            type: 'error',
          }),
        );
        handleBack();
        return;
      }

      setStep1LoadedModels((prev) => ({ ...prev, [modelName]: true }));
      addModelLayers(
        modelName,
        layers.map((l) => l.threeJsName),
        layers.map((l) => l.originalName),
      );
    },
    [addModelLayers, dispatch, handleBack],
  );

  const handleNext = () => {
    setActiveStep((prev) => Math.min(prev + 1, 1));
  };

  // Handle model loading errors
  useEffect(() => {
    if (modelLoadingError) {
      dispatch(
        toastReducer.actions.showToast({
          message: modelLoadingError,
          type: 'error',
        }),
      );
      setActiveStep(0);
      setModelLoadingError(null);
    }
  }, [modelLoadingError, dispatch, setModelLoadingError]);

  const confirmScale = (mpu: number) => {
    setMetersPerUnit(mpu);
    setScaleCalibrated(true);
    setScaleBarOpen(false);
    setScalePickActive(false);
    setPickedUnitDistance(null);
  };

  return (
    <Box
      className="project-wizard"
      sx={{ width: '100%', height: 'calc(100dvh - 64px)', overflow: 'hidden', position: 'relative' }}
    >
      {/* STEP 1: SETUP PROJECT */}
      <TabPanel value={activeStep} index={0}>
        {/* Background Scene for Step 1 — shows model preview when files are uploaded */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: isMobile ? 0 : 455,
            bottom: 0,
            background: 'transparent',
            zIndex: 0,
          }}
        >
          {hasUploadedModels ? (
            <ModelPreviewScene
              onObjectHover={noop}
              onObjectClick={noop}
              hoveredLayer={null}
              layerSettings={[]}
              hoveredLayerColor={null}
              uploadedModels={uploadedModels}
              onLayersFound={handleStep1LayersFound}
              loadedModels={step1LoadedModels}
              scalePickActive={scalePickActive}
              onScalePointsPicked={(dist) => {
                setScalePickActive(false);
                setPickedUnitDistance(dist);
              }}
              onScalePickCancel={() => setScalePickActive(false)}
            />
          ) : (
            <SimpleScene />
          )}
          {hasUploadedModels && !areModelsLoaded && (
            <div
              style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(0, 0, 0, 0.4)',
                zIndex: 200,
              }}
            >
              <LoadingSpinner size={60} color="#B3EC49" />
            </div>
          )}
        </div>

        {/* Bottom scale toolbar — all calibration UX lives here, no popups */}
        {hasUploadedModels && scaleBarOpen && (
          <div
            style={{
              position: 'absolute',
              left: 0,
              right: isMobile ? 0 : 455,
              bottom: 0,
              top: 0,
              pointerEvents: 'none',
              zIndex: 150,
            }}
          >
            <ModelScaleBottomBar
              scalePickActive={scalePickActive}
              pickedUnitDistance={pickedUnitDistance}
              onStartPick={() => setScalePickActive(true)}
              onCancelPick={() => setScalePickActive(false)}
              onCancelPickedEntry={() => setPickedUnitDistance(null)}
              onConfirm={confirmScale}
              onClose={() => setScaleBarOpen(false)}
            />
          </div>
        )}

        {/* Overlay UI for Step 1 */}
        <div style={{ position: 'relative', zIndex: 1, height: '100%', pointerEvents: 'none' }}>
          <SetupProject
            onNext={handleNext}
            onOpenScaleBar={() => setScaleBarOpen(true)}
            areModelsLoaded={areModelsLoaded}
          />
        </div>
      </TabPanel>

      {/* STEP 2: PROJECT SETTINGS (LAYERS) */}
      <TabPanel value={activeStep} index={1}>
        <div style={{ position: 'relative', zIndex: 1, height: '100%' }}>
          <ProjectSettings onNext={handleNext} onBack={handleBack} />
        </div>
      </TabPanel>
    </Box>
  );
}
